import React, { useState, useEffect } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { IconEye, IconKey } from '@wds/icons';
import { Slide, Zoom } from '@wds/motion';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { ProgressCircle } from '@wds/progress-circle';

import PropTypes from 'prop-types';
import STRINGS from './constants';

const getCookie = (name) => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
	return '';
};

const LoginPageContext = (props) => {
	const {
		siteDetails,
		ssoConnection,
		isMarketplace,
		showLoginWithGoogle,
		googleUrl,
		setCurrentView,
		nextUrl,
	} = props;

	const defaultContext = {
		email: '',
		password: '',
		mfaCode: '',
		isRememberDevice: false,
	};

	const [context, setContext] = useState(defaultContext);
	const [isLoaded, setIsLoaded] = useState(false);
	const [currentPage, setCurrentPage] = useState('email');
	const [loginError, setLoginError] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordResetMessage, setShowPasswordResetMessage] = useState(false);
	const [showPwRateLimitError, setShowPwRateLimitError] = useState(false);
	const [emailError, setEmailError] = useState(false);
	const [next, setNext] = useState('');
	const [isSliding, setIsSliding] = useState(false);
	const [isZooming, setIsZooming] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [loginToken, setLoginToken] = useState('');

	useEffect(() => {
		setIsLoaded(true);
		getNextUrl();
		fetch(reverse('api-internal-login-token'), { credentials: 'same-origin' })
			.then((r) => r.json())
			.then((data) => setLoginToken(data.token || ''))
			.catch(() => {});
	}, []);

	useEffect(() => {
		if (showPwRateLimitError) {
			setTimeout(() => {
				setShowPwRateLimitError(false);
				setShowPasswordResetMessage(false);
			}, 60000);
		}
	}, [showPwRateLimitError]);

	useEffect(() => {
		if (showPasswordResetMessage) {
			setTimeout(() => {
				setShowPasswordResetMessage(false);
			}, 60000);
		}
	}, [showPasswordResetMessage]);

	function requestLogin({ email, password }, retryToken = null) {
		const url = reverse('api-internal-login');
		const data = {
			email,
			password,
			next,
			login_token: retryToken || loginToken,
		};
		fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRFToken': getCookie('csrftoken'),
			},
			body: JSON.stringify(data),
		})
			.then((response) => response.json())
			.then((loginRequest) => {
			if (loginRequest.success === true) {
				if ((loginRequest.has_mfa_enabled || loginRequest.mfa_required) && !loginRequest.device_remembered) {
					setCurrentView('mfa');
				} else {
					window.location.replace(loginRequest.redirect_url);
				}
				} else if (
					loginRequest.error === 'too_many_attempts' ||
					loginRequest.error === 'invalid_code_locked_out'
				) {
					setCurrentView('locked-out');
				} else if (loginRequest.error === 'invalid_login_token' && retryToken === null) {
					fetch(reverse('api-internal-login-token'), { credentials: 'same-origin' })
						.then((r) => r.json())
						.then((tokenData) => {
							const newToken = tokenData.token || '';
							setLoginToken(newToken);
							requestLogin({ email, password }, newToken);
						})
						.catch(() => {
							setLoginError('invalid_login_token');
							setIsProcessing(false);
						});
				} else {
					setLoginError(loginRequest.error);
					setIsProcessing(false);
				}
			})
			.catch((error) => {
				console.error(error);
				setLoginError(STRINGS.unknownError);
			});
	}

	function requestForgottenPasswordEmail(email) {
		const url = reverse('api-internal-password-reset');
		const data = { email };
		fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRFToken': getCookie('csrftoken'),
			},
			body: JSON.stringify(data),
		})
			.then((response) => {
				if (response.status === 429) {
					setShowPasswordResetMessage(false);
					setShowPwRateLimitError(true);
				}
				return response.json();
			})
			.then((result) => {
				if (result.success === true) {
					setShowPasswordResetMessage(true);
				}
			})
			.catch((error) => {
				setLoginError(`unknown_error: ${error}`);
			});
	}

	const getNextUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('next')) {
			setNext(urlParams.get('next'));
		} else if (nextUrl) {
			setNext(nextUrl);
		}
	};

	const isValidEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const onHandleContextChange = (key) => (e) => {
		setLoginError('');
		setContext({ ...context, [key]: e.target.value });
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const onHandleLogin = () => {
		setLoginError('');
		setIsProcessing(true);
		if (showPassword) {
			togglePasswordVisibility();
		}
		requestLogin({
			email: context.email,
			password: context.password,
		});
	};

	const onHandlePageChange = (newPage) => {
		if (currentPage === 'email') {
			if (!isValidEmail(context.email)) {
				setEmailError(true);
				return;
			}
		}
		if (currentPage === 'password' && showPasswordResetMessage) {
			setShowPasswordResetMessage(false);
		}
		setEmailError(false);
		setLoginError('');
		setCurrentPage(newPage);
	};

	const onHandleForgotPassword = () => {
		requestForgottenPasswordEmail(context.email);
	};

	const handleGoogleLogin = () => {
		window.location.href = googleUrl;
	};

	const inputColor = loginError === 'invalid_credentials' ? 'error' : '';

	return (
		<Box
			display='flex'
			justifyContent='center'
			alignItems='center'
			style={{ minHeight: '90vh' }}
			role='main'
			aria-labelledby='login-page-title'
		>
			<Box
				display='flex'
				justifyContent='space-around'
			>
				<Zoom in={isLoaded} onEnter={() => setIsZooming(true)} onExited={() => setIsZooming(false)}>
					<Box>
						{currentPage !== 'mfa' && (
							<Box mb={4}>
								<Box style={{ width: '440px', margin: 'auto' }}>
									<img
										className='sma-logo-powered'
										src='/media/img/smapply/surveymonkey-apply.png'
										alt='SurveyMonkey Apply logo'
										style={{ maxWidth: '330px' }}
									/>
								</Box>
							</Box>
						)}
						<Box
							style={{
								width: '440px',
								boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
							}}
						>
							<Box
								style={{
									backgroundColor: 'white',
									margin: 'auto',
									minHeight: (loginError && currentPage === 'password') ? 410 : 320,
								}}
								py='4'
								px='6'
								role='region'
								aria-labelledby={
									currentPage === 'email' ? 'email-section-title' : 'password-section-title'
								}
							>
								<Zoom
									in={isLoaded}
									onEnter={() => setIsZooming(true)}
									onExited={() => setIsZooming(false)}
								>
									<Box style={{
										minHeight: (loginError && currentPage === 'password') ? 410 : 320,
										position: isSliding || isZooming ? 'relative' : 'static',
									}}
									>
										<Slide
											in={isLoaded && currentPage === 'email'}
											speed={currentPage === 'email' ? 'slow' : undefined}
											timeout={currentPage === 'email' ? undefined : 150}
											direction='right'
											onEnter={() => setIsSliding(true)}
											onExited={() => setIsSliding(false)}
											alwaysRenderContent
										>
											<Box style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}>
												{!isMarketplace && (
													<Box
														display='flex'
														justifyContent='end'
													>
														{
															siteDetails?.registrationOpen && (
																<>
																	{STRINGS.dontHaveAccount}
																	<Box style={{ width: '5px' }} />
																	<Typography
																		color='link'
																		component='a'
																		onClick={() => setCurrentView('register')}
																		aria-label='Register for an account'
																	>
																		{STRINGS.register}
																	</Typography>
																</>
															)
														}
													</Box>
												)}
												<Box mt='3' style={{ marginBottom: '20px' }}>
													<Typography
														variant='pageTitle'
														id='email-section-title'
													>
														{STRINGS.login}
													</Typography>
												</Box>
												<Box
													display='flex'
													flexDirection='column'
													gap={4}
												>
													{emailError && (
														<Box
															p={4}
															style={{
																backgroundColor: '#AC145A',
																borderRadius: '5px',
																color: '#FFFFFF',
															}}
															role='alert'
															aria-live='assertive'
														>
															{STRINGS.invalid_email}
														</Box>
													)}
													<Input
														value={context.email}
														onChange={onHandleContextChange('email')}
														id='email-field'
														name='username'
														type='email'
														label={STRINGS.emailAddress}
														color={inputColor}
														stretched
														onKeyPress={(e) => {
															if (e.key === 'Enter' && context.email !== '') {
																onHandlePageChange('password');
															}
														}}
														autoFocus
														autoComplete='username'
														autoCapitalize='none'
														autoCorrect='off'
														aria-required='true'
													/>
													<Button
														id='next-button'
														size='lg'
														onClick={() => onHandlePageChange('password')}
														disabled={context.email === ''}
														aria-disabled={context.email === ''}
													>
														{STRINGS.next}
													</Button>
												</Box>
											</Box>
										</Slide>
										<Slide
											in={isLoaded && currentPage === 'password'}
											speed={currentPage === 'password' ? 'slow' : undefined}
											timeout={currentPage === 'password' ? undefined : 150}
											direction='left'
											onEnter={() => setIsSliding(true)}
											onExited={() => setIsSliding(false)}
										>
											<Box style={{
												position: isSliding ? 'absolute' : 'static',
												width: '100%',
												minHeight: loginError ? 410 : 320,
											}}
											>
												<Box
													display='flex'
													justifyContent='flex-start'
												>
													<Typography
														color='link'
														component='a'
														href='#'
														onClick={() => onHandlePageChange('email')}
														aria-label='Go back to email input'
													>
														&lt;
														{' '}
														{STRINGS.back}
													</Typography>
												</Box>
												<Box my='3'>
													<Typography
														variant='pageTitle'
														id='password-section-title'
													>
														{STRINGS.passwordLabel}
													</Typography>
												</Box>
												<Box
													display='flex'
													flexDirection='column'
													gap={4}
												>
													<Typography
														variant='bodySm'
													>
														{context.email}
													</Typography>
													<input
														type='email'
														name='username'
														autoComplete='username'
														value={context.email}
														readOnly
														aria-hidden='true'
														tabIndex={-1}
														style={{
															position: 'absolute',
															opacity: 0,
															height: 0,
															pointerEvents: 'none',
														}}
													/>
													{loginError && (
														<Box
															p={4}
															style={{
																backgroundColor: '#AC145A',
																borderRadius: '5px',
																color: '#FFFFFF',
															}}
															role='alert'
															aria-live='assertive'
														>
															{STRINGS[loginError]}
														</Box>
													)}
													{showPwRateLimitError && (
														<Box
															p={4}
															style={{
																backgroundColor: '#AC145A',
																borderRadius: '5px',
																color: '#FFFFFF',
															}}
															role='alert'
															aria-live='assertive'
														>
															{STRINGS.rateLimitError}
														</Box>
													)}
													{showPasswordResetMessage && (
														<Box
															p={4}
															style={{
																backgroundColor: '#007500',
																borderRadius: '5px',
																color: '#FFFFFF',
															}}
															role='status'
															aria-live='polite'
														>
															{STRINGS.forgotPasswordMessage}
														</Box>
													)}
													<InputGroup
														label={STRINGS.enterPassword}
														color={inputColor}
														stretched
													>
														<Input
															value={context.password}
															onChange={onHandleContextChange('password')}
															id='password-field'
															name='password'
															type={showPassword ? 'text' : 'password'}
															color={inputColor}
															onKeyPress={(e) => {
																if (e.key === 'Enter' && context.password !== '') {
																	onHandleLogin();
																}
															}}
															autoFocus
															autoComplete='current-password'
															aria-required='true'
														/>
														<InputGroupAddon>
															<IconEye
																onClick={togglePasswordVisibility}
																aria-label={
																	showPassword ? 'Hide password' : 'Show password'
																}
															/>
														</InputGroupAddon>
													</InputGroup>
													<Button
														id='next-button'
														size='lg'
														onClick={onHandleLogin}
														disabled={context.password === '' || isProcessing}
														aria-disabled={context.password === '' || isProcessing}
													>
														{!isProcessing ? (
															STRINGS.login
														) : (
															<ProgressCircle size='sm' continuous />
														)}
													</Button>
													{!showPasswordResetMessage ? (
														<Typography
															color='link'
															component='a'
															href='#'
															onClick={() => onHandleForgotPassword()}
															aria-label='Forgot password'
														>
															{STRINGS.forgotPassword}
														</Typography>
													) : (
														<Typography
															color='success'
															weight='medium'
														>
															{STRINGS.requestSent}
														</Typography>
													)}
												</Box>
											</Box>
										</Slide>
									</Box>
								</Zoom>
							</Box>
							{(currentPage !== 'mfa' && !isMarketplace) && (
								<Box
									style={{
										width: '100%',
										backgroundColor: 'white',
										margin: 'auto',
									}}
									role='region'
									aria-labelledby='alternative-login-options'
								>
									<Box style={{ margin: 'auto' }} py={4} px={6}>
										<Box mb={4}>
											<Typography
												variant='bodySm'
												weight='medium'
												id='alternative-login-options'
											>
												{STRINGS.orLoginWith}
											</Typography>
										</Box>
										{ssoConnection?.name && (
											<Box display='flex' my={4}>
												<Button
													color='alt'
													id='sso-connection-button'
													size='lg'
													href={ssoConnection?.url}
													stretched
													aria-label={`Login with ${ssoConnection?.name}`}
												>
													<Box
														display='grid'
														alignItems='center'
														gridTemplateColumns='auto 1fr'
														gap={2}
														p={3}
													>
														<IconKey title='key-icon' />
														<Typography
															variant='bodySm'
															weight='medium'
														>
															{ssoConnection?.name}
														</Typography>
													</Box>
												</Button>
											</Box>
										)}
										{showLoginWithGoogle && (
											<Box
												display='flex'
												flexDirection='column'
												gap={4}
												alignItems='center'
												mb={4}
											>
												{ssoConnection?.name && (
													<Typography
														variant='bodySm'
														weight='medium'
													>
														{STRINGS.or}
													</Typography>
												)}
												<Button
													color='alt'
													size='md'
													label='google signin'
													onClick={() => handleGoogleLogin()}
													aria-label='Login with Google'
												>
													<Box m={2}>
														<img
															src='/media/img/icons/logo-google-g.png'
															width='25px'
															height='25px'
															alt='Google single sign-on'
														/>
													</Box>
												</Button>
												<Typography
													variant='caption'
												>
													{STRINGS.loginPage.loginWithGoogle}
												</Typography>
											</Box>
										)}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</Zoom>
			</Box>
		</Box>
	);
};

LoginPageContext.propTypes = {
	siteDetails: PropTypes.object.isRequired,
	ssoConnection: PropTypes.object,
	isMarketplace: PropTypes.bool.isRequired,
	showLoginWithGoogle: PropTypes.bool,
	googleUrl: PropTypes.string.isRequired,
	setCurrentView: PropTypes.func.isRequired,
	nextUrl: PropTypes.string,
};
LoginPageContext.defaultProps = { ssoConnection: {}, showLoginWithGoogle: true, nextUrl: '/' };

export default LoginPageContext;
